import { UsersEffects } from './../users/users.effects';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { Store } from '@ngrx/store';
import { ErrorsType } from '../../enum/errors.type';
import { AppState } from '../../interfaces/app-state.interface';
import { ResearchActions } from './research.action';
import { BackendService } from '../../services/backend/backend.service';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { RoutesTable } from '../../resources/routes-table.model';
import { SuitabilityProfilesEnum } from '../../enum/suitability/suitability-profiles.enum';
import { RealtimeService } from '../../services/realtime/realtime.service';
import { RealtimeHub } from '../../enum/realtime/realtime-hub.enum';
import { ResearchAdviceLite } from '../../models/research/research-opportunity.model';
import { UtilitiesService } from '../../services/utilities.service';
import { OrderMarketTypeEnum } from '../../enum/orders/market-type.enum';
import { IApiQueryListResponse } from '../../interfaces/api/api-query-list-response/api-query-list-response.interface';
import { RecommendationCategory } from '../../models/research/research-category.model';
import { of } from 'rxjs/observable/of';
import { Observer } from 'rxjs';
import { UsersAction } from '../users/users.actions';
import { withLatestFrom, exhaustMap, share, mergeMap, switchMap, filter, map } from 'rxjs/operators';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/retry';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/withLatestFrom';
import 'rxjs/add/operator/first';

@Injectable()
export class ResearchEffects {

    public index$ = this.store.select((state) => state.research.index);
    public indexQuote$ = this.store.select((state) => state.research.indexQuote);
    public taxTable$ = this.store.select((state) => state.research.taxTable);
    public indexAgregated$ = this.store.select((state) => state.research.indexAgregated);
    public categories$ = this.store.select((state) => state.research.categories);
    public authenticationForcing$ = this.usersEffects.forceAuthenticationRequest$
    .pipe(
        filter((action) => {
            return action.type.equals(UsersAction.forcedAuthenticationSuccefully);
        }),
    );

    /**
     * Obtem lista de indexers.
     * @method getIndex
     */
    @Effect()
    public getIndex$ = this.actions$
    .pipe(
    ofType(ResearchActions.getIndex),
    withLatestFrom(this.index$),
    exhaustMap(([action, index]) => {
        if (index && index.length) {
            return of({ type: ResearchActions.getIndexSuccess, payload: index });
        }
        return this.dataService.get<DefaultResponse>('research/index')
            .map((result) => {
                if (result.hasAlerta) {
                    return {
                        type: ResearchActions.getIndexError,
                        payload: {
                            type: ErrorsType.Warning,
                            value: result.messages,
                        },
                    };
                }
                if (result.hasError) {
                    return {
                        type: ResearchActions.getIndexError,
                        payload: {
                            type: ErrorsType.Error,
                            value: result.messages,
                        },
                    };
                }
                if (result.ok && result.hasValue) {
                    return {
                        type: ResearchActions.getIndexSuccess,
                        payload: result.value,
                    };
                }

                return {
                    type: ResearchActions.getIndexSuccess,
                    payload: result.value,
                };

            })
            .catch((error) => of({ type: ResearchActions.getIndexError, payload: error }));
        }),
        share());

    /**
     * Obtem um produto especifico
     * @method getIndexQuote
     */
    @Effect()
    public getIndexQuote$ = this.actions$
    .pipe(
    ofType(ResearchActions.getIndexQuote),
    withLatestFrom(this.indexQuote$),
    exhaustMap(([action, indexQuote]) => {

        if (indexQuote && indexQuote.value && indexQuote.value.length) {
            return of({ type: ResearchActions.getIndexQuoteSuccess, payload: indexQuote });
        }

        return this.dataService.get<DefaultResponse>(`research/indexquote?&$inlinecount=allpages`)
        .map((result) => {
            if (!result.ok) {
                return {
                    type: ResearchActions.getIndexQuoteError,
                    payload: {
                        type: ErrorsType.Warning,
                        value: result.messages,
                    },
                };
            }

            return {
                type: ResearchActions.getIndexQuoteSuccess,
                payload: result,
            };

        })
        .catch((error) => of({ type: ResearchActions.getIndexQuoteError, payload: error }));
    }),
    share());

    @Effect()
    public getTaxTable$ = this.actions$
      .pipe(
        ofType(ResearchActions.getTaxTable),
        withLatestFrom(this.taxTable$),
        exhaustMap(([action, taxTable]) => {

            if (taxTable && taxTable.length) {
                return of({
                    type: ResearchActions.getTaxTableSuccess,
                    payload: taxTable,
                });
            }
            return this.dataService.get<DefaultResponse>(`research/tax`)
                .map((result) => {
                    if (result.hasError) {
                        return {
                            type: ResearchActions.getTaxTableError,
                            payload: {
                                value: result.messages,
                            },
                        };
                    }
                    return {
                        type: ResearchActions.getTaxTableSuccess,
                        payload: result.value,
                    };
                })
                .retry(5)
                .catch((error) => of({
                    type: ResearchActions.getTaxTableError,
                    payload: error,
                }));
        }),
        share());

    /**
     * Obtem lista de indexers agregated
     * @method getIndexAgregated
     */
    @Effect()
    public getIndexAgregated$ = this.actions$
    .pipe(
    ofType(ResearchActions.getIndexAgregated),
    withLatestFrom(this.indexAgregated$),
    exhaustMap(([action, indexAgregated]) => {

        if (indexAgregated && indexAgregated.length) {
            return of({
                type: ResearchActions.getIndexAgregatedSuccess,
                payload: indexAgregated,
            });
        }

        return this.dataService.get<DefaultResponse>('fixedincome/indexer?type=System')
            .map((result) => {
                if (!result.ok) {
                    return {
                        type: ResearchActions.getIndexAgregatedError,
                        payload: {
                            type: ErrorsType.Error,
                            value: result.messages,
                        },
                    };
                }

                return {
                    type: ResearchActions.getIndexAgregatedSuccess,
                    payload: result.value,
                };

            })
            .catch((error) => of({
                type: ResearchActions.getIndexAgregatedError,
                payload: error,
            }));
    }),
    share());

    /**
     * Obtem o banda de preços (stop gain e stop loss) para um ativo
     * @method getPriceBands
     */
    @Effect()
    public getPriceBands$ = this.actions$
    .pipe(
    ofType(ResearchActions.getPriceBands),
    withLatestFrom(this.store.select((state) => state.research.priceBand)),
    mergeMap(([action, priceBand]) => {
        const normalizedStockId = action.payload.symbol.toUpperCase();

        if (priceBand && priceBand[normalizedStockId] && priceBand[normalizedStockId].operationType === action.payload.operationType) {
            return of({
                type: ResearchActions.getPriceBandsSuccess,
                payload: priceBand[normalizedStockId],
            });
        }

        const uri = RoutesTable.composeUri(RoutesTable.research.routes.getPriceBands, action.payload.symbol, action.payload.operationType);
        return this.dataService.get<DefaultResponse>(uri)
        .map((result) => {
            if (result.ok) {
                return {
                    type: ResearchActions.getPriceBandsSuccess,
                    payload: result.value,
                };
            }
            return {
                type: ResearchActions.getPriceBandsError,
                payload: {
                    type: ErrorsType.Error,
                    value: result.messages,
                },
            };
        })
        .catch((error) => of({
            type: ResearchActions.getPriceBandsError,
            payload: error,
        }));
    }),
    share());

    /**
     * Obtem as oportunidades com os dados já reduzidos
     * @method getOpportunities
     */
    @Effect()
    public getOpportunities$: Observable<any> = this.actions$
    .pipe(
    ofType(ResearchActions.getOpportunities),
    exhaustMap((action) => {
        return this.dataService.get<DefaultResponse>(RoutesTable.research.routes.getOpportunities.uri)
        .map((result) => {
            if (result.ok) {
                const filteredValue = this.filterUserAdvice(result.value);
                return { type: ResearchActions.getOpportunitiesSuccess, payload: filteredValue };
            }
            return {
                type: ResearchActions.getOpportunitiesError,
                payload: result.value,
            };
        })
        .catch((error) => of({
            type: ResearchActions.getOpportunitiesError,
            payload: error,
        }));
    }),
    share());

    /**
     * Obtem uma opportunidade com os dados já reduzidos
     */
    @Effect()
    public getOpportunity$: Observable<any> = this.actions$
    .pipe(
    ofType(ResearchActions.getOpportunity),
    switchMap((action) => {
        return this.dataService.get<DefaultResponse>(RoutesTable.composeUri(RoutesTable.research.routes.getOpportunity, action.payload))
        .map((result) => {
            if (result.ok) {
                return {
                    type: ResearchActions.getOpportunitySuccess,
                    payload: new ResearchAdviceLite(result.value),
                };
            }
            return {
                type: ResearchActions.getOpportunityError,
                payload: result.value,
            };

        })
        .catch((error) => of({
            type: ResearchActions.getOpportunityError,
            payload: error,
        }));
    }),
    share());

    /**
     * Obtem uma recomendção em tempo real
     * @method researchAdvicesSubscribe
     */
    @Effect({ dispatch: false })
    public researchAdvicesSubscribe$: Observable<any> = this.actions$
    .pipe(
    ofType(ResearchActions.reasearchAdvicesSubscribe),
    filter((action) => this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(RealtimeHub.Research) === true ),
    map((action) => {
        this.realtimeService.subscribeToResearchAdvices()
        .withLatestFrom(this.store.select((state) => state), (body, state) => {
            const newBody = this.utilsService.normalize(body);
            return {
                profile: state.suitability.profile,
                orders: state.stocksExchange.wallet.validStrategies,
                body: new ResearchAdviceLite(newBody),
            };
        })
        .filter(({profile, orders, body}) => !!(body.instrument && body.instrument.stress))
        .subscribe(({profile, orders, body}) => {

            const hasEqualPositionStrategy = Object.keys(orders)
            .map((order) => orders[order])
            .filter((order) => order.symbol === body.symbolID)
            .some((order) => order.isDayTrade === body.isDayTrade);

            if (body && !hasEqualPositionStrategy) {
                const noConservative = profile && profile.profile !== SuitabilityProfilesEnum.Conservative;
                if (profile && noConservative && profile.profile !== SuitabilityProfilesEnum.NoProfile) {

                    if (body.instrument.typeMarket === OrderMarketTypeEnum.Future && !body.symbolID.match(/(WIN)|(WDO)/g)) {
                        if (profile.profile === SuitabilityProfilesEnum.Bold) {
                            this.store.dispatch({ type: ResearchActions.reasearchAdvicesSubscribeSuccess, payload: body });
                        }
                    } else {
                        this.store.dispatch({ type: ResearchActions.reasearchAdvicesSubscribeSuccess, payload: body });
                    }
                } else if (!profile) { // Usuário anônimo
                    this.store.dispatch({ type: ResearchActions.reasearchAdvicesSubscribeSuccess, payload: body });
                }
            }

        });
    }),
    share());

    /**
     * Carregar categorias com recomendações
     */
    @Effect()
    public loadCategories$ = this.actions$
    .pipe(
        ofType(ResearchActions.loadCategories),
        exhaustMap((action) => {
            const uri = RoutesTable.research.routes.getCategories.uri;
            return this.dataService.get<IApiQueryListResponse<RecommendationCategory>>(uri)
            .pipe(
                map((result) => {
                    if (result.ok) {
                        return {
                            type: ResearchActions.loadCategoriesSuccess,
                            payload: this.normalizeCategoryRecommendations(result.value),
                        };
                    }
                    return {
                        type: ResearchActions.loadCategoriesError,
                        payload: result.messages,
                    };
                }),
            );
        }),
        share());

    constructor(
    public actions$: Actions<ResearchActions>,
    public dataService: BackendService,
    public store: Store<AppState>,
    public realtimeService: RealtimeService,
    public utilsService: UtilitiesService,
    private usersEffects: UsersEffects,
    ) { }

    /**
     * Filtra apenas as oportunidades válidas.
     * @param opportunities Lista de oportunidades recebidas.
     */
    public filterUserAdvice(opportunities: any[]): ResearchAdviceLite[] {
        return opportunities
        .filter((opportunity) => opportunity.instrument && opportunity.instrument.stress)
        .map((opportunity) => new ResearchAdviceLite(opportunity));
    }
    /**
     * Normaliza as recomendações das categorias (principalmente pelo instrument)
     * @param categories lista de categorias
     */
    private normalizeCategoryRecommendations(categories: RecommendationCategory[]) {
        return categories.map((category) => {
            category.recommendations = category.recommendations.map((recomendation) => new ResearchAdviceLite(recomendation));
            return category;
        });
    }
}
